import { endOfDay } from 'date-fns';
import styled from 'styled-components';
import * as yup from 'yup';

import Button from 'components/shared/Button';
import DatePicker from 'components/shared/form/DatePicker';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input, { InputStyles } from 'components/shared/form/Input';
import { FormProvider, useForm } from 'context/form';
import { FormHelpers, FormMeta, FormValues } from 'context/form/types';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { parseDate, simpleDashDate } from 'lib/date';
import { DenialList } from 'models/DenialList';
import { DenialListItem, Sex, SEX_DISPLAY } from 'models/DenialListItem';
import {
  AdminDenialListItemMutationParams,
  createAdminDenialListItem,
  updateAdminDenialListItem,
} from 'services/api/admin/denialListItems';
import { useToastActions } from 'stores/toastStore';
import { BodySmall, BodySmallBold } from 'styles/typography';

const REASON_MAX_LENGTH = 5000;

type SexOption = { value: Sex; label: string };
const SEX_OPTIONS: SexOption[] = (Object.keys(SEX_DISPLAY) as Sex[]).map((value) => ({
  value,
  label: SEX_DISPLAY[value],
}));

type AdminDenialListEntryFormValues = {
  patientName: string;
  dateOfBirth: Date | null;
  sex: SexOption | null;
  reason: string;
  dateAdded: Date | null;
};

type AdminDenialListEntryFormMeta = {
  isSubmitting: boolean;
};

// "Not in the future" is checked against today evaluated at validation time (not module load) — the
// Date Added default is `new Date()` created when the form opens, which would fail a max() pinned at
// import time and wedge the form as permanently invalid.
const notInFuture = (value?: Date | null) => !value || value <= endOfDay(new Date());

const adminDenialListEntrySchema = yup.object({
  patientName: yup.string().trim().required(),
  dateOfBirth: yup
    .date()
    .nullable()
    .required()
    .typeError('Enter a valid date')
    .test('dob-not-future', 'Date of birth cannot be in the future', notInFuture),
  sex: yup.object({ value: yup.string(), label: yup.string() }).nullable().required(),
  reason: yup.string().nullable().max(REASON_MAX_LENGTH),
  dateAdded: yup
    .date()
    .nullable()
    .typeError('Enter a valid date')
    .test('date-added-not-future', 'Date added cannot be in the future', notInFuture),
});

export type AdminDenialListEntryFormProps = {
  denialList: DenialList;
  denialListItem?: DenialListItem;
  onClose: () => void;
};

// Inline add/edit patient form rendered inside the admin Global Denial List slideout. Unlike the
// customer DenialListEntryModal, the admin flow has no "Review Details" step (per the mockup) —
// Cancel / Add Patient (or Confirm on edit) submit directly.
export default function AdminDenialListEntryForm({
  denialList,
  denialListItem,
  onClose,
}: AdminDenialListEntryFormProps) {
  const isEdit = !!denialListItem;

  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();

  const { mutate } = useMutateWithToast({
    // clientId is only needed to resolve the list on create; update operates on the item id, so it
    // is stripped from the update payload (the update endpoint doesn't permit it).
    mutationFn: ({ clientId, ...params }: AdminDenialListItemMutationParams) =>
      isEdit
        ? updateAdminDenialListItem({ id: denialListItem.id, ...params })
        : createAdminDenialListItem({ clientId, ...params }),
  });

  const handleSubmit = (
    values: FormValues<AdminDenialListEntryFormValues>,
    _meta: FormMeta<AdminDenialListEntryFormMeta>,
    helpers: FormHelpers<AdminDenialListEntryFormValues, AdminDenialListEntryFormMeta>
  ) => {
    helpers.setMeta('isSubmitting', true);

    const params: AdminDenialListItemMutationParams = {
      // clientId lets the API find-or-create this client's active list, covering both an existing
      // list and a client whose list doesn't exist yet.
      clientId: denialList.client?.id ?? '',
      patientName: (values.patientName ?? '').trim(),
      dateOfBirth: simpleDashDate(values.dateOfBirth) ?? '',
      sex: (values.sex?.value ?? 'unknown') as Sex,
      reason: values.reason?.trim() || null,
      dateAdded: simpleDashDate(values.dateAdded),
    };

    mutate(params, {
      onSuccess: () => {
        // Also invalidate the customer items cache so the client's Denial List page reflects the
        // admin's change (cross-surface consistency, per the ticket AC).
        invalidateQueries({ adminDenialListItems: true, adminDenialLists: true, denialListItems: true });
        addToast({
          text: isEdit
            ? `${params.patientName} successfully updated`
            : `${params.patientName} successfully added to denial list`,
        });
        onClose();
      },
      onSettled: () => {
        helpers.setMeta('isSubmitting', false);
      },
    });
  };

  return (
    <FormProvider<AdminDenialListEntryFormValues, AdminDenialListEntryFormMeta>
      onSubmit={handleSubmit}
      yupSchema={adminDenialListEntrySchema}
      initialValues={{
        patientName: denialListItem?.patientName ?? '',
        dateOfBirth: parseDate(denialListItem?.dateOfBirth ?? null),
        sex: SEX_OPTIONS.find((option) => option.value === denialListItem?.sex) ?? null,
        reason: denialListItem?.reason ?? '',
        // Date Added defaults to today (server time), editable on create + edit.
        dateAdded: isEdit ? parseDate(denialListItem?.dateAdded) : new Date(),
      }}
      initialMeta={{ isSubmitting: false }}>
      <AdminDenialListEntryFields isEdit={isEdit} onClose={onClose} />
    </FormProvider>
  );
}

function AdminDenialListEntryFields({ isEdit, onClose }: { isEdit: boolean; onClose: () => void }) {
  const { values, errors, setValue, validateField, valid, dirty, meta, submitForm } = useForm<
    AdminDenialListEntryFormValues,
    AdminDenialListEntryFormMeta
  >();

  const isSubmitting = !!meta.isSubmitting;

  return (
    <Container>
      <SectionTitle>{isEdit ? 'Edit Patient' : 'Add New Patient'}</SectionTitle>
      <Description>
        Complete the fields below to {isEdit ? 'edit a patient on' : 'add a new patient to'} the denial list
      </Description>

      <Fields>
        <Field>
          <FieldLabel>Patient Name</FieldLabel>
          <Input
            value={values.patientName ?? ''}
            onChange={(e) => setValue('patientName', e.target.value)}
            placeholder='Patient Name'
            disabled={isSubmitting}
          />
        </Field>

        <HalfField>
          <FieldLabel>Date of Birth</FieldLabel>
          <DatePicker
            selected={values.dateOfBirth ?? null}
            onChange={(date) => setValue('dateOfBirth', date)}
            onBlur={() => validateField('dateOfBirth', values)}
            placeholder='MM/DD/YYYY'
            disabled={isSubmitting}
            maxDate={new Date()}
            peekNextMonth
            showMonthDropdown
            showYearDropdown
            dropdownMode='select'
            $hasError={!!errors.dateOfBirth}
            $fullWidth
          />
        </HalfField>

        <Field>
          <FieldLabel>Gender</FieldLabel>
          <Select<SexOption>
            options={SEX_OPTIONS}
            value={values.sex ?? null}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
            onChange={(option) => setValue('sex', option)}
            placeholder='Gender'
            isSearchable={false}
            disabled={isSubmitting}
            dataTestId='gender-select'
          />
        </Field>

        <Field>
          <FieldLabel>Reason</FieldLabel>
          <TextArea
            value={values.reason ?? ''}
            onChange={(e) => setValue('reason', e.target.value)}
            placeholder='Add any additional context here'
            maxLength={REASON_MAX_LENGTH}
            rows={3}
            disabled={isSubmitting}
          />
        </Field>

        <HalfField>
          <FieldLabel>Date Added</FieldLabel>
          <DatePicker
            selected={values.dateAdded ?? null}
            onChange={(date) => setValue('dateAdded', date)}
            placeholder='MM/DD/YYYY'
            disabled={isSubmitting}
            maxDate={new Date()}
            $fullWidth
          />
        </HalfField>
      </Fields>

      <Actions>
        <Button variant='ghost' onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant='primary'
          onClick={() => submitForm()}
          loading={isSubmitting}
          disabled={!valid || !dirty || isSubmitting}>
          {isEdit ? 'Confirm' : 'Add Patient'}
        </Button>
      </Actions>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const SectionTitle = styled(BodySmallBold)`
  font-size: 16px;
  margin-bottom: 8px;
`;

const Description = styled(BodySmall)`
  display: block;
  margin-bottom: 24px;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 40px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

// Date fields are half-width in the design (the date control occupies the left half of its row);
// text/select/textarea fields are full-width.
const HalfField = styled(Field)`
  width: 50%;
`;

const TextArea = styled.textarea`
  ${InputStyles}
  resize: vertical;
  font-family: inherit;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;
